import type { IWorkAreaDataChange, IWorkItemEvent } from '@/services/WorkItemApi';
import {
  subscribeToDataChanges,
  subscribeToDataChangesOf,
  subscribeToEvents,
} from '@/services/workItemSubscriptions';
import { usesNativeCloudGateway } from '@/data-sources/nativeCloudGateway';
import type { DatasetSyncController } from './datasetSync';
import {
  desktopRealtimeAnswerPending,
  desktopRealtimeAvailable,
  isRealtimeRefused,
  onAnyRealtimeConnectionChange,
  onRealtimeRefusedChange,
  realtimeConnectionOf,
  whenDesktopRealtimeAnswered,
} from '@/services/realtimeClients';

/**
 * The shared work-item event hub.
 *
 * Every view that re-queries when something changes wants the same thing: a doorbell per work
 * area. Opening one subscription per view multiplies sockets, server-side listeners and replay
 * frames by the number of open views, which is how a tab with a roadmap, an activity list and two
 * dashboard cards ended up asking the backend for four copies of the same stream. This hub keeps
 * exactly one subscription per work area, however many listeners it has, and hands each of them
 * the events plus the health of the stream.
 *
 * It is deliberately live-only: a doorbell caller re-runs its own query and never reads the
 * payload, so replaying a work area's history would cost a frame per historical row per tab.
 * Callers that apply deltas incrementally (the work-item store) keep their own replaying
 * subscription — it multiplexes over the same socket.
 *
 * Each topic opens two streams, because one of them cannot see everything:
 *
 *   - `workItemEvents` — the delta protocol, emitted by the work-item service. Carries what changed,
 *     so a store can apply it without re-reading.
 *   - `workAreaDataChanges` — emitted from a database trigger, so it also covers what the service
 *     never sees: comments and other trigger-written journal rows, workflow-state renames, releases,
 *     item-type edits, and anything written by the CLI or a background job. Carries only the fact
 *     that the work area moved.
 *
 * A doorbell listener wants both and cannot tell them apart; it re-reads either way. The topic is
 * `live` while either stream is up, because either one is enough to hear about a change.
 */

export type RealtimeStatus = 'connecting' | 'live' | 'down';

export interface WorkAreaRealtimeListener {
  onEvent?: (event: IWorkItemEvent) => void;
  /** A change with no delta to apply — a comment, a release, a renamed state. Re-read. */
  onDataChange?: (change: IWorkAreaDataChange) => void;
  /** Called on every change of stream health. */
  onStatus?: (status: RealtimeStatus) => void;
  /**
   * Called when the stream comes back after having been down — by socket reconnect or by the hub
   * reopening it. Whatever happened during the gap was never delivered, so a listener that tracks
   * changes has to re-read. Not called on the first connect: the listener has just loaded.
   */
  onResume?: () => void;
}

const REOPEN_BASE_DELAY_MS = 1_000;
const REOPEN_MAX_DELAY_MS = 30_000;

/** The two server streams a topic carries. See the note at the top of the file. */
type StreamKind = 'events' | 'dataChanges';

const STREAM_KINDS: readonly StreamKind[] = ['events', 'dataChanges'];

/**
 * Each stream reconnects on its own schedule. Sharing one would mean a stream the server does not
 * serve — a browser tab that outlived a backend release, say — dragging the working one down with it
 * on every retry.
 */
interface TopicStream {
  kind: StreamKind;
  dispose: (() => void) | null;
  attempt: number;
  reopenTimer: ReturnType<typeof setTimeout> | null;
}

interface RealtimeTopic {
  workAreaId: string;
  listeners: Set<WorkAreaRealtimeListener>;
  streams: TopicStream[];
  status: RealtimeStatus;
  /** Set the moment a stream goes down, cleared once listeners have been told to re-read. */
  missedEvents: boolean;
}

const topics = new Map<string, RealtimeTopic>();
let disposeConnectionWatch: (() => void) | null = null;

type DeliveryTapType = (workAreaId: string) => void;
const deliveryTaps = new Set<DeliveryTapType>();

/**
 * RES-1 (T-0673): told the work area of every frame any topic's stream delivers. The desktop's
 * launch smoke reports from it that a live event arrived on each connection; nothing else listens.
 */
export function tapRealtimeDeliveries(tap: DeliveryTapType): () => void {
  deliveryTaps.add(tap);
  return () => {
    deliveryTaps.delete(tap);
  };
}

function delivered(topic: RealtimeTopic): void {
  deliveryTaps.forEach((tap) => tap(topic.workAreaId));
}

function watchConnection(): void {
  if (disposeConnectionWatch) return;
  // T-0549 RTC-3: a connection's drop marks only the work areas it holds; another connection's
  // topics stay live.
  const stopConnectionWatch = onAnyRealtimeConnectionChange((connected, connection) => {
    topics.forEach((topic) => {
      if (realtimeConnectionOf(topic.workAreaId).connection !== connection) return;
      if (connected) markLive(topic);
      else markDown(topic);
    });
  });
  // I-2199: a refused socket never comes up, so the topics on it are told they are down once, and
  // every listener is told to re-read on a timer for as long as it stays refused.
  const stopRefusedWatch = onRealtimeRefusedChange(() => {
    if (!isRealtimeRefused()) {
      stopRefusedRefresh();
      return;
    }
    topics.forEach((topic) => {
      if (realtimeConnectionOf(topic.workAreaId).connection === 'main') markDown(topic);
    });
    startRefusedRefresh();
  });
  startRefusedRefresh();
  disposeConnectionWatch = () => {
    stopConnectionWatch();
    stopRefusedWatch();
    stopRefusedRefresh();
  };
}

/**
 * I-2199: how often a listener on a refused socket (4403, a view as another role) is told to
 * re-read. With no doorbell, nothing else would tell a stream-fed view that anything changed
 * before its own slow backstop poll.
 */
export const REFUSED_REFRESH_INTERVAL_MS = 30_000;
let refusedRefreshTimer: ReturnType<typeof setInterval> | null = null;

function startRefusedRefresh(): void {
  if (refusedRefreshTimer || !isRealtimeRefused()) return;
  refusedRefreshTimer = setInterval(() => {
    topics.forEach((topic) => topic.listeners.forEach((listener) => listener.onResume?.()));
    doorbellGroups.forEach((group) => group.listeners.forEach((listener) => listener.onResume?.()));
  }, REFUSED_REFRESH_INTERVAL_MS);
}

function stopRefusedRefresh(): void {
  if (refusedRefreshTimer) clearInterval(refusedRefreshTimer);
  refusedRefreshTimer = null;
}

function releaseConnectionWatch(): void {
  if (topics.size > 0 || doorbellGroups.size > 0 || !disposeConnectionWatch) return;
  disposeConnectionWatch();
  disposeConnectionWatch = null;
}

function setStatus(topic: RealtimeTopic, status: RealtimeStatus): void {
  if (topic.status === status) return;
  topic.status = status;
  topic.listeners.forEach((listener) => listener.onStatus?.(status));
}

function markDown(topic: RealtimeTopic): void {
  topic.missedEvents = true;
  setStatus(topic, 'down');
}

function markLive(topic: RealtimeTopic, stream?: TopicStream): void {
  // A stream that delivered is a stream that works, so only its own backoff resets.
  if (stream) stream.attempt = 0;
  const resumed = topic.missedEvents;
  topic.missedEvents = false;
  setStatus(topic, 'live');
  if (resumed) topic.listeners.forEach((listener) => listener.onResume?.());
}

/**
 * I-2223: the server refused the stream because this reader may not read the work area (a removed
 * member, a lowered role). That answer does not change by asking again, so the stream is left closed
 * instead of being reopened every 30 s for as long as the tab lives; the dataset's own poll and the
 * next page load still ask. A drop of any other kind keeps its backoff.
 */
function isForbiddenRefusal(error: unknown): boolean {
  const entries = Array.isArray(error) ? error : [error];
  return entries.some((entry) => {
    const candidate = entry as { message?: unknown; extensions?: { code?: unknown } } | null;
    return (
      candidate?.extensions?.code === 'FORBIDDEN' ||
      (typeof candidate?.message === 'string' && /\bforbidden\b/i.test(candidate.message))
    );
  });
}

function stopRefused(topic: RealtimeTopic, stream: TopicStream): void {
  markDown(topic);
  stream.dispose?.();
  stream.dispose = null;
}

function openStream(topic: RealtimeTopic, stream: TopicStream): void {
  const reopening = topic.missedEvents;
  const fail = (error?: unknown) =>
    isForbiddenRefusal(error) ? stopRefused(topic, stream) : scheduleReopen(topic, stream);
  stream.dispose =
    stream.kind === 'events'
      ? subscribeToEvents(
          topic.workAreaId,
          null,
          (event) => {
            markLive(topic, stream);
            delivered(topic);
            topic.listeners.forEach((listener) => listener.onEvent?.(event));
          },
          fail,
        )
      : subscribeToDataChanges(
          topic.workAreaId,
          (change) => {
            markLive(topic, stream);
            delivered(topic);
            topic.listeners.forEach((listener) => listener.onDataChange?.(change));
          },
          fail,
        );
  // The resume fires on reopen rather than waiting for proof the stream works, because what it
  // means is "there was a gap, re-read" — which is already true. The status stays whatever the
  // socket says it is; claiming `live` here would be claiming something unobserved.
  if (reopening) {
    topic.missedEvents = false;
    topic.listeners.forEach((listener) => listener.onResume?.());
  }
}

function scheduleReopen(topic: RealtimeTopic, stream: TopicStream): void {
  if (!topics.has(topic.workAreaId)) return;
  markDown(topic);
  stream.dispose?.();
  stream.dispose = null;
  // I-2199: a refused socket (4403) is final for the credential; a reopen would only be refused again.
  if (isRealtimeRefused() && realtimeConnectionOf(topic.workAreaId).connection === 'main') return;
  if (stream.reopenTimer) return;
  // `graphql-ws` retries the socket on its own; this covers the case it gives up on, such as a
  // rejected token, where the next attempt has to re-read credentials from scratch.
  const delay = Math.min(REOPEN_MAX_DELAY_MS, REOPEN_BASE_DELAY_MS * 2 ** stream.attempt);
  stream.attempt += 1;
  stream.reopenTimer = setTimeout(() => {
    stream.reopenTimer = null;
    if (!topics.has(topic.workAreaId) || stream.dispose) return;
    openStream(topic, stream);
  }, delay);
}

function closeTopic(topic: RealtimeTopic): void {
  topics.delete(topic.workAreaId);
  topic.streams.forEach((stream) => {
    if (stream.reopenTimer) clearTimeout(stream.reopenTimer);
    stream.reopenTimer = null;
    stream.dispose?.();
    stream.dispose = null;
  });
  releaseConnectionWatch();
}

/**
 * True when live updates for this work area come from a subscription at all: the main socket, or —
 * for a work area behind the desktop's cloud gateway — the desktop relay, on a build that has one
 * (T-0549).
 */
export function supportsWorkAreaRealtime(workAreaId: string): boolean {
  return !usesNativeCloudGateway(workAreaId) || desktopRealtimeAvailable();
}

/**
 * A work area behind the cloud gateway that the desktop has not yet said it can relay. It is not
 * served now, but may be once the answer is in, so a subscriber waits for it rather than deciding
 * once and never looking again.
 */
function mayGainRealtime(workAreaId: string): boolean {
  return usesNativeCloudGateway(workAreaId) && desktopRealtimeAnswerPending();
}

/**
 * How often a view still polls once the doorbell covers it.
 *
 * Between `workItemEvents` and `workAreaDataChanges` the hub hears about everything a checkpoint can
 * see, and a gap in either announces itself, so the poll is no longer how a change arrives. What is
 * left for it to cover is the case nothing else can: a doorbell that is broken in a way neither end
 * noticed. A minute is short enough that such a view is never meaningfully stale, and long enough
 * that a tab full of views stops being a steady stream of requests.
 */
export const REALTIME_BACKSTOP_POLL_INTERVAL_MS = 60_000;

/**
 * The interval a view should actually use. Work areas the streams serve get the backstop; anything
 * the doorbell cannot reach — a local project, a work area behind the native cloud gateway — keeps
 * its own interval, because for those the poll is not a backstop, it is the delivery mechanism.
 */
export function datasetPollIntervalMs(
  workAreaIds: readonly string[],
  unstreamedIntervalMs: number,
): number {
  const known = workAreaIds.filter(Boolean);
  // I-2199: with the socket refused nothing rings, so the poll is the delivery mechanism again.
  return known.length > 0 && !isRealtimeRefused() && known.every(supportsWorkAreaRealtime)
    ? REALTIME_BACKSTOP_POLL_INTERVAL_MS
    : unstreamedIntervalMs;
}

/**
 * Joins the work area's shared event stream. The first listener opens it; the last one to leave
 * closes it.
 */
export function subscribeToWorkAreaRealtime(
  workAreaId: string,
  listener: WorkAreaRealtimeListener,
): () => void {
  if (!supportsWorkAreaRealtime(workAreaId)) {
    if (!mayGainRealtime(workAreaId)) return () => undefined;
    // Asked before the answer: join once it says yes (T-0549), so the work area is not left on its
    // poll for the rest of the session.
    let joined: (() => void) | null = null;
    const stopWaiting = whenDesktopRealtimeAnswered(() => {
      if (supportsWorkAreaRealtime(workAreaId)) {
        joined = subscribeToWorkAreaRealtime(workAreaId, listener);
      }
    });
    return () => {
      stopWaiting();
      joined?.();
      joined = null;
    };
  }
  watchConnection();
  let topic = topics.get(workAreaId);
  if (!topic) {
    topic = {
      workAreaId,
      listeners: new Set(),
      streams: STREAM_KINDS.map((kind) => ({
        kind,
        dispose: null,
        attempt: 0,
        reopenTimer: null,
      })),
      status: 'connecting',
      missedEvents: false,
    };
    topics.set(workAreaId, topic);
    topic.streams.forEach((stream) => openStream(topic!, stream));
  }
  const joined = topic;
  joined.listeners.add(listener);
  // A view mounted onto an already-open stream never sees a transition, so it is told where the
  // stream stands right now rather than being left to infer it from the next one.
  if (joined.status !== 'connecting') listener.onStatus?.(joined.status);
  return () => {
    joined.listeners.delete(listener);
    if (joined.listeners.size === 0) closeTopic(joined);
  };
}

/**
 * One doorbell for a whole list of work areas (T-0607 IT-21 to IT-24): a list across work areas
 * would otherwise open an events stream and a data-changes stream for every work area it reads, 400
 * at 200. This is one `workAreasDataChanges` stream naming them all, on the main connection, shared
 * by every view that names the same set and closed with the last. A work area behind a desktop
 * connection is not on that socket and keeps `subscribeToWorkAreaRealtime`.
 *
 * It carries the data-changes doorbell only. The trigger behind it covers the work-item journal as
 * well as everything else a checkpoint can see, so a work item write rings it as it rings the events
 * stream; the view's own poll is the backstop as ever. A stream that was down and came back says so
 * (`onResume`), and a listener re-reads.
 */
export interface WorkAreasDoorbellListener {
  onDataChange?: (change: IWorkAreaDataChange) => void;
  onResume?: () => void;
}

interface IDoorbellGroup {
  key: string;
  workAreaIds: string[];
  listeners: Set<WorkAreasDoorbellListener>;
  dispose: (() => void) | null;
  attempt: number;
  reopenTimer: ReturnType<typeof setTimeout> | null;
  /** The stream went down; listeners are told once it is back. */
  missed: boolean;
  stopWatching: (() => void) | null;
}

const doorbellGroups = new Map<string, IDoorbellGroup>();

function openDoorbellGroup(group: IDoorbellGroup): void {
  const reopening = group.missed;
  group.dispose = subscribeToDataChangesOf(
    group.workAreaIds,
    (change) => {
      group.attempt = 0;
      resumeDoorbellGroup(group);
      deliveryTaps.forEach((tap) => tap(change.workAreaId));
      group.listeners.forEach((listener) => listener.onDataChange?.(change));
    },
    () => scheduleDoorbellReopen(group),
  );
  // Reopening after a gap is itself the reason to re-read, as it is for a work area's topic.
  if (reopening) resumeDoorbellGroup(group);
}

function resumeDoorbellGroup(group: IDoorbellGroup): void {
  if (!group.missed) return;
  group.missed = false;
  group.listeners.forEach((listener) => listener.onResume?.());
}

function scheduleDoorbellReopen(group: IDoorbellGroup): void {
  if (!doorbellGroups.has(group.key)) return;
  group.missed = true;
  group.dispose?.();
  group.dispose = null;
  if (isRealtimeRefused()) return;
  if (group.reopenTimer) return;
  const delay = Math.min(REOPEN_MAX_DELAY_MS, REOPEN_BASE_DELAY_MS * 2 ** group.attempt);
  group.attempt += 1;
  group.reopenTimer = setTimeout(() => {
    group.reopenTimer = null;
    if (!doorbellGroups.has(group.key) || group.dispose) return;
    openDoorbellGroup(group);
  }, delay);
}

function closeDoorbellGroup(group: IDoorbellGroup): void {
  doorbellGroups.delete(group.key);
  if (group.reopenTimer) clearTimeout(group.reopenTimer);
  group.reopenTimer = null;
  group.dispose?.();
  group.dispose = null;
  group.stopWatching?.();
  group.stopWatching = null;
  releaseConnectionWatch();
}

/** Whether `workAreaId` is on the main connection's socket, where the one stream can serve it. */
function onMainConnection(workAreaId: string): boolean {
  return (
    realtimeConnectionOf(workAreaId).connection === 'main' && supportsWorkAreaRealtime(workAreaId)
  );
}

export function subscribeToWorkAreasDoorbell(
  workAreaIds: readonly string[],
  listener: WorkAreasDoorbellListener,
): () => void {
  const ids = Array.from(new Set(workAreaIds.filter(Boolean))).sort();
  if (ids.length === 0) return () => undefined;
  const key = ids.join(',');
  let group = doorbellGroups.get(key);
  if (!group) {
    const opened: IDoorbellGroup = {
      key,
      workAreaIds: ids,
      listeners: new Set(),
      dispose: null,
      attempt: 0,
      reopenTimer: null,
      missed: false,
      stopWatching: null,
    };
    // The main socket dropping marks the stream missed; its coming back tells the listeners.
    opened.stopWatching = onAnyRealtimeConnectionChange((connected, connection) => {
      if (connection !== 'main') return;
      if (!connected) opened.missed = true;
      else resumeDoorbellGroup(opened);
    });
    doorbellGroups.set(key, opened);
    watchConnection();
    openDoorbellGroup(opened);
    group = opened;
  }
  const joined = group;
  joined.listeners.add(listener);
  return () => {
    joined.listeners.delete(listener);
    if (joined.listeners.size === 0) closeDoorbellGroup(joined);
  };
}

export interface RealtimeDoorbellOptions {
  /** Work areas the view reads. A generic view can span several. */
  workAreaIds: readonly string[];
  /**
   * Resolved on every ring rather than captured, so a caller holding its controller in a ref can
   * swap it without re-attaching the doorbell.
   */
  controllers: () => readonly (DatasetSyncController | null | undefined)[];
  onEvent?: (event: IWorkItemEvent) => void;
}

/**
 * Wires a view's `DatasetSyncController`s to the hub: any change rings them at once, and a stream
 * that dropped and came back rings them once more for what it missed.
 *
 * The view keeps its interval poll, but only as a backstop. Between the two streams the hub carries
 * everything a checkpoint can see, so the poll is no longer how a change normally arrives — it is
 * what covers the case where the doorbell itself was broken and nobody noticed.
 */
export function attachRealtimeDoorbell({
  workAreaIds,
  controllers,
  onEvent,
}: RealtimeDoorbellOptions): () => void {
  const streamed = Array.from(new Set(workAreaIds.filter(Boolean))).filter(
    (id) => supportsWorkAreaRealtime(id) || mayGainRealtime(id),
  );
  if (streamed.length === 0) return () => undefined;

  const ring = () => controllers().forEach((controller) => void controller?.request('realtime'));

  // T-0607 IT-21: two or more work areas on the main connection share one stream, however many; the
  // rest (one work area, or one behind a desktop connection) keep their own topics. A caller that
  // wants the events themselves (`onEvent`) keeps the per-work-area streams, which carry them.
  const grouped = onEvent ? [] : streamed.filter(onMainConnection);
  const together = grouped.length >= 2 ? grouped : [];
  const alone = streamed.filter((workAreaId) => !together.includes(workAreaId));

  const disposers = alone.map((workAreaId) =>
    subscribeToWorkAreaRealtime(workAreaId, {
      onEvent: (event) => {
        onEvent?.(event);
        ring();
      },
      // Same ring. A view re-reads its checkpoint either way, and the two streams overlap — a work
      // item write produces both an event and a table change — so the controller's coalescing is
      // what keeps that from being two round trips.
      onDataChange: ring,
      onResume: ring,
    }),
  );
  if (together.length > 0) {
    disposers.push(subscribeToWorkAreasDoorbell(together, { onDataChange: ring, onResume: ring }));
  }

  return () => disposers.forEach((dispose) => dispose());
}

/** Test seam: drops every topic and the connection watch. */
export function resetWorkAreaRealtimeForTest(): void {
  Array.from(topics.values()).forEach((topic) => {
    topic.listeners.clear();
    closeTopic(topic);
  });
  topics.clear();
  Array.from(doorbellGroups.values()).forEach((group) => {
    group.listeners.clear();
    closeDoorbellGroup(group);
  });
  disposeConnectionWatch?.();
  disposeConnectionWatch = null;
}
